import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import AuthUtility from "../../util/AuthUtility";
import { Col, Button, Row, Container, Card, Form, InputGroup, Table, Pagination, Spinner, Badge } from 'react-bootstrap';
import { Page } from "../../dto/page/Page";
import SearchIcon from '@mui/icons-material/Search';
import { BrandEntity } from "../../dto/brand/BrandEntity";
import { SysJobRunListEntity } from "../../dto/sysjob/SysJobRunListEntity";
import { SysJobRunSearch } from "../../dto/sysjob/SysJobRunSearch";
import { onSysJobRunSearch } from "../../controller/SysJobController";

const SysJobRunList = () => {
    const navigate = useNavigate();

    // ---- Auth check
    useEffect(() => {
        if (!AuthUtility.isUserLoggedIn()) {
            navigate("/login");
            window.location.reload();
        }
    }, []);

    const [sysJobRuns, setSysJobRuns] = useState<SysJobRunListEntity>({
        data: []
    });

    const [search, setSearch] = useState<SysJobRunSearch>({
        limit: 10
    });

    // ---- Load brands
    const load = async (page: number = 0) => {
        const params: SysJobRunSearch = {
            ...search,
            offset: page
        };

        const response = await onSysJobRunSearch(params);

        if (response.status !== 0) {
            toast.error("Job уншихад алдаа гарлаа");
            return;
        }

        const pageable = response.data;

        const pageInfo: Page = {
            limit: search.limit,
            totalElements: pageable.totalElements,
            totalPages: pageable.totalPages,
            offset: page
        };

        const beans: Array<BrandEntity> = pageable.beans;

        setSysJobRuns({
            page: pageInfo,
            data: beans
        });

        setSearch(prev => ({ ...prev, offset: page }));
    };

    // ---- Search submit
    const searchSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        load(0); // reset to first page on new search
    };

    // ---- Pagination click
    const changePage = (page: number) => {
        if (!sysJobRuns.page) return;
        if (page < 0 || page >= sysJobRuns.page.totalPages) return;
        load(page);
    };

    return (
        <Container fluid>
            <Toaster position="top-right" />

            <h3 className="mb-3">
                <a onClick={() => navigate(-1)} style={{ cursor: 'pointer' }}>
                    <big>&#8592;</big>
                </a> Job log
            </h3>

            {/* Search Card */}
            <Row className="mb-3">
                <Col md={12}>
                    <Card>
                        <Card.Header>
                            <Form onSubmit={searchSubmit}>
                                <Row>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>Эхлэх огноо</Form.Label>
                                            <Form.Control
                                                type="date"
                                                size="sm"
                                                value={search.startDate || ""}
                                                onChange={(e) =>
                                                    setSearch({
                                                        ...search,
                                                        startDate: e.target.value || undefined
                                                    })
                                                }
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>Дуусах огноо</Form.Label>
                                            <Form.Control
                                                type="date"
                                                size="sm"
                                                value={search.endDate || ""}
                                                onChange={(e) =>
                                                    setSearch({
                                                        ...search,
                                                        endDate: e.target.value || undefined
                                                    })
                                                }
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>Багцлах</Form.Label>
                                            <InputGroup>
                                                <Form.Select
                                                    size="sm"
                                                    value={search.limit}
                                                    onChange={(e) =>
                                                        setSearch({
                                                            ...search,
                                                            limit: parseInt(e.target.value),
                                                        })
                                                    }
                                                >
                                                    <option value="10">10</option>
                                                    <option value="25">25</option>
                                                    {/* <option value="0">Бүгд</option> */}
                                                </Form.Select>
                                                <Button variant="primary" size="sm" type="submit">
                                                    <SearchIcon fontSize="small" />
                                                </Button>
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Header>

                        {/* Table */}
                        <Card.Body>
                            <Table hover responsive>
                                <thead>
                                    <tr>
                                        <th>Created At</th>
                                        <th>Started At</th>
                                        <th>Finished At</th>
                                        <th>Code</th>
                                        <th>Status</th>
                                        <th>Duration ms</th>
                                        <th>Processed count</th>
                                        <th>Success count</th>
                                        <th>Fail count</th>
                                        <th>Node id</th>
                                        <th>Error</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sysJobRuns.data.length === 0 && (
                                        <tr>
                                            <td className="text-muted">
                                                Илэрц олдсонгүй
                                            </td>
                                        </tr>
                                    )}

                                    {sysJobRuns.data.map((data) => (
                                        <tr key={data.id}>
                                            <td>{data.createdAt
                                            ? new Date(data.createdAt).toLocaleString()
                                            : "-"}</td>
                                            <td>{data.startedAt
                                                        ? new Date(data.startedAt).toLocaleString()
                                            : "-"}</td>
                                            <td>{data.finishedAt
                                            ? new Date(data.finishedAt).toLocaleString()
                                            : "-"}</td>
                                            <td>{data.jobCode}</td>
                                            <td>{data.status}</td>
                                            <td>{data.durationMs}</td>
                                            <td>{data.processedCount}</td>
                                            <td>{data.successCount}</td>
                                            <td>{data.failCount}</td>
                                            <td>{data.nodeId}</td>
                                            <td>{data.errorMsg}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {sysJobRuns.page && sysJobRuns.page.totalPages > 1 && (() => {
                const page = sysJobRuns.page;   // now TypeScript knows it's NOT undefined

                return (
                    <Row>
                        <Col className="d-flex justify-content-start">
                            <Pagination>
                                <Pagination.First
                                    disabled={page.offset === 0}
                                    onClick={() => changePage(0)}
                                />

                                <Pagination.Prev
                                    disabled={page.offset === 0}
                                    onClick={() => changePage(page.offset - 1)}
                                />

                                {Array.from({ length: page.totalPages })
                                    .slice(
                                        Math.max(page.offset - 2, 0),
                                        Math.min(page.offset + 3, page.totalPages)
                                    )
                                    .map((_, idx) => {
                                        const pageNumber =
                                            Math.max(page.offset - 2, 0) + idx;

                                        return (
                                            <Pagination.Item
                                                key={pageNumber}
                                                active={pageNumber === page.offset}
                                                onClick={() => changePage(pageNumber)}
                                            >
                                                {pageNumber + 1}
                                            </Pagination.Item>
                                        );
                                    })}

                                <Pagination.Next
                                    disabled={page.offset + 1 >= page.totalPages}
                                    onClick={() => changePage(page.offset + 1)}
                                />

                                <Pagination.Last
                                    disabled={page.offset + 1 >= page.totalPages}
                                    onClick={() => changePage(page.totalPages - 1)}
                                />
                            </Pagination>
                        </Col>
                    </Row>
                );
            })()}
        </Container>
    );
};

export default SysJobRunList;
